import { __assign, __rest } from "tslib";
import React, { useState, useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { BiDataHook, BiLoggerContextProvider } from '../../../biLogger';
import LoadingIndicator from '../../../LoadingIndicator';
import Separator from '../../../Separator';
import DaEditorWrapper from '../../../DaEditorWrapper/LoadableDaEditorWrapper';
import FallbackEditor from '../../../DaEditorWrapper/Fallback';
import { EditorFlavourConfigs } from '../../../DaEditorWrapper/config';
import { useEditor } from '../../../DaEditorWrapper/hooks';
import mergeTheme from '../../../DaEditorWrapper/Themes/mergeTheme';
import LazyPlaceholder from '../LazyPlaceholder';
import LoadingPlaceholder from '../LoadingPlaceholder';
import CommentBoxFooter from '../CommentBoxFooter';
import commentTextTheme from '../../../DaEditorWrapper/Themes/Viewer/comments.scss';
import commentEditorTheme from '../../../DaEditorWrapper/Themes/Editor/comments.scss';
import s from '../../CommentBox.scss';
var editorTheme = mergeTheme(commentTextTheme, commentEditorTheme);
var CommentBoxEditor = React.forwardRef(function (_a, ref) {
    var id = _a.id, commentableItemId = _a.commentableItemId, commentableType = _a.commentableType, autoFocus = _a.autoFocus, compact = _a.compact, forceLoadOnMount = _a.forceLoadOnMount, initialContent = _a.initialContent, editorClassName = _a.editorClassName, editorWrapperClassName = _a.editorWrapperClassName, footerClassName = _a.footerClassName, errorMsg = _a.errorMsg, isBusy = _a.isBusy, isError = _a.isError, isEdit = _a.isEdit, isReply = _a.isReply, loadingState = _a.loadingState, onReady = _a.onReady, onChange = _a.onChange, onFocus = _a.onFocus, onCancel = _a.onCancel, onSubmit = _a.onSubmit, onPlaceholderClick = _a.onPlaceholderClick, tabIndex = _a.tabIndex, restProps = __rest(_a, ["id", "commentableItemId", "commentableType", "autoFocus", "compact", "forceLoadOnMount", "initialContent", "editorClassName", "editorWrapperClassName", "footerClassName", "errorMsg", "isBusy", "isError", "isEdit", "isReply", "loadingState", "onReady", "onChange", "onFocus", "onCancel", "onSubmit", "onPlaceholderClick", "tabIndex"]);
    var t = useTranslation().t;
    var _b = useState(false), isFocused = _b[0], setIsFocused = _b[1];
    var _c = useState(false), isFallback = _c[0], setIsFallback = _c[1];
    var _d = useState(false), hasSwitchedMode = _d[0], setHasSwitchedMode = _d[1];
    var _e = useState(true), isEmpty = _e[0], setIsEmpty = _e[1];
    var _f = useState(undefined), fallbackInitialContent = _f[0], setFallbackInitialContent = _f[1];
    var onContentChange = function (editorState) {
        setIsEmpty(editorState.isEmpty);
        setFallbackInitialContent(editorState.rawContentState);
        onChange && onChange(editorState);
    };
    var _g = useEditor({
        autoFocus: autoFocus,
        onEditorReady: function (toolbars, api) {
            onReady && onReady(toolbars, api);
        },
        onEditorChange: onContentChange,
    }), editorProps = _g.editorProps, editorToolbars = _g.editorToolbars;
    var placeholder = isReply
        ? t('commentbox.editor.placeholder.reply')
        : t('commentbox.editor.placeholder');
    // We don't want the editor to rerender with each keypress
    var Fallback = useMemo(function () { return (React.createElement(FallbackEditor, { value: fallbackInitialContent, onChange: onContentChange, onReady: function (toolbars, api) {
            onReady && onReady({}, api);
        } })); }, [
        isFallback,
        onContentChange
    ]);
    // Memo because we want to stop the toolbar from rendering too much
    // this also fixes double click to use the toolbar as it updates as you click
    // and loses the event
    var Footer = useMemo(function () { return (React.createElement(CommentBoxFooter, { toolbar: editorToolbars === null || editorToolbars === void 0 ? void 0 : editorToolbars.INSERT_PLUGIN, isEditorBusy: isBusy || false, isEditorEmpty: isEmpty, isEdit: isEdit, isFallback: isFallback, onCancel: onCancel, onSubmit: onSubmit, onToggleFallback: function () {
            setIsFallback(!isFallback);
            setHasSwitchedMode(true);
        }, tabIndexBase: tabIndex + 1, compact: compact, className: classnames(s['commentbox-footer'], s['comment-animation'], footerClassName) })); }, [
        compact,
        editorToolbars,
        footerClassName,
        isBusy,
        isEdit,
        isEmpty,
        isFallback,
        onCancel,
        onSubmit,
        tabIndex,
    ]);
    if (isError) {
        // TODO get designers to make nice errors
        errorMsg && alert(errorMsg);
    }
    return (React.createElement("div", { ref: ref, className: classnames(s['commentbox-editor-wrapper'], editorWrapperClassName, isFocused && s['focused']), "data-hook": BiDataHook.COMMENT_EDITOR },
        isBusy && React.createElement(LoadingIndicator, { className: s['loading'] }),
        !isFallback && (React.createElement(DaEditorWrapper, __assign({ staticComponent: function () { return React.createElement(LazyPlaceholder, { isReply: isReply }); }, loadingComponent: function () { return React.createElement(LoadingPlaceholder, { isReply: isReply }); }, forceLoadOnMount: forceLoadOnMount || hasSwitchedMode, flavour: EditorFlavourConfigs.COMMENT, editorKey: id, theme: editorTheme, initialContent: fallbackInitialContent || initialContent, placeholder: placeholder, "aria-label": restProps['aria-label'], "aria-labelledby": restProps['aria-labelledby'], tabIndex: tabIndex, filterPaste: true, className: classnames(s['commentbox-editor'], editorClassName), onFocus: function () {
                setIsFocused(true);
                onFocus && onFocus();
            }, onBlur: function () { return setIsFocused(false); }, onKeyboardSubmit: onSubmit, onStaticComponentClick: onPlaceholderClick }, editorProps))),
        loadingState === 2 /* DONE */ && isFallback && Fallback,
        React.createElement(Separator, { className: s['separator'] }),
        loadingState === 2 /* DONE */ && (React.createElement(BiLoggerContextProvider, { value: {
                itemid: typeof commentableItemId === 'string'
                    ? parseInt(commentableItemId)
                    : commentableItemId,
                typeid: commentableType,
            } }, Footer))));
});
CommentBoxEditor.displayName = 'CommentBoxEditor';
export default CommentBoxEditor;
//# sourceMappingURL=CommentBoxEditor.js.map